import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BaseDashboard from '../components/BaseDashboard';
import { createApiUrl } from '../utils/apiConfig';
import { assetFileDisplayName } from '../utils/assetDisplayName';
import { Button } from 'react-bootstrap';
import StandardPagination from '../components/StandardPagination';
import './SchoolAdminDashboard.css';
import { useNavigate } from 'react-router-dom';
import RenewalModal from '../components/RenewalModal';
import { getMinWorkshopDate, validateWorkshopDate } from '../utils/workshopValidation';
import './Subscriptions.css';
import ImpersonationBanner from '../components/ImpersonationBanner';

const SchoolAdminDashboard = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [workshopPlans, setWorkshopPlans] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const teachersPerPage = 12;
  
  // Search and pagination state for teachers
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('');
  const [teacherCurrentPage, setTeacherCurrentPage] = useState(1);
  const [teacherItemsPerPage, setTeacherItemsPerPage] = useState(12);
  
  // Search and pagination state for students
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [studentItemsPerPage, setStudentItemsPerPage] = useState(12);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [orgCodeInput, setOrgCodeInput] = useState('');
  const [orgCodeError, setOrgCodeError] = useState('');
  const [joiningOrg, setJoiningOrg] = useState(false);
  const [isAutoPopup, setIsAutoPopup] = useState(false); // Track if this is auto-popup or manual
  const [workshopErrors, setWorkshopErrors] = useState({});

  // Determine default tab based on user status
  const getDefaultTab = () => {
    if (!user) return 'students';
    
    // Default to students tab for School Admins
    return 'students';
  };

  const [activeTab, setActiveTab] = useState('students');

  // Update activeTab when user data is loaded
  useEffect(() => {
    if (user) {
      setActiveTab(getDefaultTab());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultTab intentionally excluded
  }, [user]);

  // Update tabs when user data changes
  useEffect(() => {
    if (user) {
      const newTabs = getTabs();
      // If current active tab is 'plans' but it's no longer available, switch to 'subscriptions'
      if (activeTab === 'plans' && !newTabs.find(tab => tab.id === 'plans')) {
        setActiveTab('subscriptions');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTabs intentionally excluded
  }, [user, activeTab]);

  // Show join organization modal for standalone school admins with no subscriptions
  useEffect(() => {
        if (user) {
            // Check each condition individually
      const isSchAdmin = user.isSchAdmin;
      const hasOrgCode = user.orgCode && user.orgCode !== '';
      const hasSubscriptions = user.subscriptions && user.subscriptions.length > 0;
      const hasSchSubscriptions = user.schSubscriptions && user.schSubscriptions.length > 0;
      
                       if (isSchAdmin && !hasOrgCode && !hasSubscriptions && !hasSchSubscriptions) {
                          setShowJoinModal(true);
             setIsAutoPopup(true); // This is an auto-popup
                        } else {
                        }
    } else {
          }
  }, [user]);

  // Dynamic tabs based on whether school is organization-bound
  const getTabs = () => {
    const baseTabs = [
      {id: 'students', label: 'All Students'},
      {id: 'teachers', label: 'All Teachers'},
      {id: 'workshops', label: 'Workshops'},
      {id: 'subscriptions', label: 'Subscriptions'},
    ];
    
    // Only show plans tab for standalone schools (not organization-bound)
    if (!user?.orgCode) {
      baseTabs.push({id: 'plans', label: 'Plans'});
    }
    
    return baseTabs;
  };
  
  const tabs = getTabs();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch school admin data
      const schResponse = await axios.get(createApiUrl('/api/schooladmin'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      // Set the school admin user data
      setUser(schResponse.data);

      // Fetch teachers for this school
      try {
        const teachersResponse = await axios.get(createApiUrl('/api/school/teachers'), {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        });
        
        if (teachersResponse.data.success) {
          setTeachers(teachersResponse.data.teachers);
        }
      } catch (teacherError) {
              }

      // Fetch students for this school
      try {
        const studentsResponse = await axios.get(createApiUrl('/api/school/students'), {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        });
        
        if (studentsResponse.data.success) {
          setStudents(studentsResponse.data.students);
        }
      } catch (studentError) {
        console.error('Error fetching school students:', studentError);
        setStudents([]);
      }

      // Check for available org subscriptions if school admin has orgCode but no subscriptions
      if (schResponse.data.orgCode && (!schResponse.data.subscriptions || schResponse.data.subscriptions.length === 0)) {
                try {
          const checkResponse = await axios.post(createApiUrl('/api/check-org-subscriptions'), {}, {
            headers: {
              'Accept': 'application/json'
            },
            withCredentials: true
          });
          
                    if (checkResponse.data.success) {
                        // Refresh user data to get the new subscription
            const updatedResponse = await axios.get(createApiUrl('/api/schooladmin'), {
              headers: {
                'Accept': 'application/json'
              },
              withCredentials: true
            });
            setUser(updatedResponse.data);
          } else {
                      }
        } catch (checkError) {
                  }
      } else {
              }

      // Fetch subscription details if user has subscription IDs
      const currentUser = user || schResponse.data;
      if (currentUser.subscriptions && currentUser.subscriptions.length > 0) {
        
        try {
          // The subscriptions are already SchSubscription objects from the API
          // No need to fetch them again
          const subscriptionDetails = currentUser.subscriptions;

          setSubscriptions(subscriptionDetails);
        } catch (subscriptionError) {
                    // Set empty array if subscription fetching fails
          setSubscriptions([]);
        }
      } else {
        setSubscriptions([]);
      }

      // Fetch plans
      const plansResponse = await axios.get(createApiUrl('/api/plans'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      setPlans(plansResponse.data || []);

      // Fetch workshop plans
      const workshopResponse = await axios.get(createApiUrl('/api/workshop-plans'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      setWorkshopPlans(workshopResponse.data || []);

      setLoading(false);
    } catch (error) {
                  // Handle specific error cases
      if (error.response?.status === 403) {
        // Get user data to determine the correct redirect
        try {
          const authResponse = await axios.get(createApiUrl('/api/auth/check'));
          const userData = authResponse.data.user;
          
          if (userData.userType === 'student' || userData.role === 'student' || userData.studentCode) {
            window.location.href = '/student-dashboard';
            return;
          } else if (userData.isOrgAdmin) {
            window.location.href = '/org-admin-dashboard';
            return;
          } else if (userData.isAdmin) {
            window.location.href = '/admin-dashboard';
            return;
          } else {
            window.location.href = '/teacher-dashboard';
            return;
          }
        } catch (authError) {
                    // Fallback redirect
          window.location.href = '/dashboard';
          return;
        }
      }
      
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      setLoading(false);

      // Set fallback data to prevent complete failure
      setSubscriptions([]);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Refresh data when the page becomes visible (e.g., returning from basket)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const handleAddToBasket = async (planId, workshopDate = null) => {
    try {
      // For school admins, use the sch-add-to-basket endpoint
      // School admins can only have ONE plan in their basket
      const response = await axios.post(createApiUrl(`/api/sch-add-to-basket/${planId}`), {
        quantity: 1,
        workshopDate: workshopDate
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Navigate to basket page
        window.location.href = '/basket';
      } else {
        alert(response.data.message || 'You can only have one subscription plan in your basket. Please remove the existing plan before adding another.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'You can only have one subscription plan in your basket. Please remove the existing plan before adding another.';
      alert(errorMessage);
    }
  };

  const handlePurchaseSubscription = () => {
    // Navigate to Plans tab instead of subscriptions page
    setActiveTab('plans');
  };

  const _handleJoinOrganization = () => {
    setShowJoinModal(true);
  };

  const handleRenewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRenewalModal(true);
  };

  const handleRenewalSuccess = () => {
    fetchData(); // Refresh the data after successful renewal
  };

  const handleCloseRenewalModal = () => {
    setShowRenewalModal(false);
    setSelectedSubscription(null);
  };

  const handleSwitchToTeacher = async () => {
    try {
      // Update role in database
      const response = await axios.post(createApiUrl('/api/switch-role'), {
        role: 'teacher'
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
                // Refresh user data to get updated currentActiveRole
        try {
          const authResponse = await axios.get(createApiUrl('/api/auth/check'), {
            withCredentials: true
          });
          
          if (authResponse.data.user) {
            // Update the user in localStorage to ensure consistency
            localStorage.setItem('user', JSON.stringify(authResponse.data.user));
            // Force a page reload to ensure the AuthContext gets the updated user data
            window.location.href = '/teacher-dashboard';
          } else {
            // Fallback to navigate if auth check fails
            navigate('/teacher-dashboard');
          }
        } catch (authError) {
                    // Fallback to navigate if auth check fails
          navigate('/teacher-dashboard');
        }
      } else {
                // Error handling removed for production
      }
    } catch (error) {
            // Error handling removed for production
    }
  };

  const handleAutoRenewChange = async (subscriptionId, isChecked) => {
    try {
      
      const response = await axios.patch(createApiUrl(`/api/subscriptions/${subscriptionId}`), {
        autoRenew: isChecked
      }, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      if (response.status === 200) {
        // Update the local state to reflect the change
        setSubscriptions(prevSubscriptions => 
          prevSubscriptions.map(sub => 
            sub._id === subscriptionId 
              ? { ...sub, autoRenew: isChecked }
              : sub
          )
        );
      }
    } catch (error) {
            // Error handling removed for production
    }
  };

  const handleViewTeacherDetails = (teacher) => {
    navigate(`/user/${teacher._id}?from=school-admin`);
  };

  // Search and pagination functions for teachers
  const getFilteredTeachers = () => {
    if (!teacherSearchQuery) return teachers;
    return teachers.filter(teacher => 
      teacher.contactName?.toLowerCase().includes(teacherSearchQuery.toLowerCase()) ||
      teacher.email?.toLowerCase().includes(teacherSearchQuery.toLowerCase()) ||
      teacher.teacherCode?.toLowerCase().includes(teacherSearchQuery.toLowerCase()) ||
      teacher.schoolName?.toLowerCase().includes(teacherSearchQuery.toLowerCase())
    );
  };

  const getPaginatedTeachers = () => {
    const filtered = getFilteredTeachers();
    const startIndex = (teacherCurrentPage - 1) * teacherItemsPerPage;
    const endIndex = startIndex + teacherItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleTeacherSearch = (e) => {
    e.preventDefault();
    setTeacherCurrentPage(1);
  };

  const handleTeacherPageChange = (page) => {
    setTeacherCurrentPage(page);
  };

  // Search and pagination functions for students
  const getFilteredStudents = () => {
    if (!studentSearchQuery) return students;
    return students.filter(student => 
      student.contactName?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.studentCode?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.schoolName?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.yearGrade?.toLowerCase().includes(studentSearchQuery.toLowerCase())
    );
  };

  const getPaginatedStudents = () => {
    const filtered = getFilteredStudents();
    const startIndex = (studentCurrentPage - 1) * studentItemsPerPage;
    const endIndex = startIndex + studentItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleStudentSearch = (e) => {
    e.preventDefault();
    setStudentCurrentPage(1);
  };

  const handleStudentPageChange = (page) => {
    setStudentCurrentPage(page);
  };

  // Legacy pagination logic (keeping for compatibility)
  const indexOfLastTeacher = currentPage * teachersPerPage;
  const indexOfFirstTeacher = indexOfLastTeacher - teachersPerPage;
  const _currentTeachers = teachers.slice(indexOfFirstTeacher, indexOfLastTeacher);
  const _totalPages = Math.ceil(teachers.length / teachersPerPage);

  const _handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleJoinOrgSubmit = async (e) => {
    e.preventDefault();
    setOrgCodeError('');
    setJoiningOrg(true);

    try {
      const response = await axios.post(createApiUrl('/api/join-org'), { orgCode: orgCodeInput });
      
      if (response.data.success) {
        setShowJoinModal(false);
        setOrgCodeInput('');
        setIsAutoPopup(false);
        
        window.location.reload();
      }
    } catch (error) {
            setOrgCodeError(error.response?.data?.message || 'Error joining organization.');
    } finally {
      setJoiningOrg(false);
    }
  };

  const handleManualJoinOrg = () => {
    setShowJoinModal(true);
    setIsAutoPopup(false);
    setOrgCodeInput('');
    setOrgCodeError('');
  };

  const handleCloseModal = () => {
    setShowJoinModal(false);
    setIsAutoPopup(false);
    setOrgCodeInput('');
    setOrgCodeError('');
  };

  const _formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const _getStatusBadge = (status) => {
    const statusClasses = {
      'active': 'badge bg-success',
      'expired': 'badge bg-warning',
      'cancelled': 'badge bg-danger',
      'error': 'badge bg-secondary'
    };
    
    return (
      <span className={statusClasses[status] || 'badge bg-secondary'}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </span>
    );
  };

  if (error) {
    return (
      <BaseDashboard
        title="School Admin Dashboard"
        tabs={tabs}
        user={user}
        loading={false}
      >
        <div className="error-message">
          <div className="alert alert-danger">
            <h4>Error Loading Dashboard</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchData}>
              Retry
            </button>
          </div>
        </div>
      </BaseDashboard>
    );
  }

  return (
    <>
    <BaseDashboard
      title="School Admin Dashboard"
      tabs={tabs}
      user={user}
      loading={loading}
      defaultActiveTab={activeTab}
      onTabChange={setActiveTab}
      switchButton={user?.isSchAdmin ? (
        <button 
          className="btn btn-sm"
          onClick={handleSwitchToTeacher}
          style={{
            backgroundColor: 'var(--lightOrange)',
            borderColor: 'var(--darkPurple)',
            color: 'var(--darkPurple)',
            fontWeight: '500',
            border: '1px solid var(--darkPurple)',
            borderRadius: '4px'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--darkPurple)';
            e.target.style.color = 'var(--lightOrange)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'var(--lightOrange)';
            e.target.style.color = 'var(--darkPurple)';
          }}
        >
          <i className="fas fa-chalkboard-teacher me-2"></i>
          Switch to Teacher
        </button>
      ) : null}
      banner={<ImpersonationBanner user={user} />}
    >
      {/* Subscriptions Tab */}
      <div tabId="subscriptions" className="subscriptions-tab-content">
        {/* Subscription Summary Card */}
        <div className="row mb-4">
          <div className="col-md-8 mx-auto">
            <div className="card subscription-summary-card">
              <div className="card-body">
                <h5 className="card-title">Subscription Summary</h5>
                
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Active Subscriptions:</strong> {subscriptions.filter(sub => sub.status === 'active').length}</p>
                    <p><strong>Total Subscriptions:</strong> {subscriptions.length}</p>
                    <p><strong>Organization Code:</strong> {user?.orgCode || 'Not joined to organization'}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Standalone Subscriptions:</strong> {subscriptions.filter(sub => !sub.orgUserId && !sub.organizationId).length}</p>
                    <p><strong>Organization Managed:</strong> {subscriptions.filter(sub => sub.orgUserId || sub.organizationId).length}</p>
                    {user?.orgCode && (
                      <p className="text-muted">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          Organization-managed subscriptions are renewed by your organization
                        </small>
                      </p>
                    )}
                    
                    {/* Join Organization Button - Right Column */}
                    {(!user?.orgCode || user?.orgCode === '') && (
                      <div style={{marginTop: '5px'}}>
                        <button 
                          className="btn btn-sm" 
                          onClick={handleManualJoinOrg}
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            color: 'var(--darkOrange)',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--darkOrange)';
                            e.target.style.color = 'var(--darkPurple)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.color = 'var(--darkOrange)';
                          }}
                        >
                          Join Organization
                        </button>
                        <p style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                          Join an organization to access their subscription benefits
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <h4 className="section-title" style={{ textAlign: 'center', marginBottom: '24px' }}>Available Subscriptions</h4>
        {(() => {
          let showPurchaseButton = false;
          if (subscriptions.length === 0) {
            showPurchaseButton = true;
          } else {
            // Find most recent active subscription
            let mostRecent = subscriptions
              .filter(sub => sub.status === 'active')
              .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
            if (!mostRecent) {
              // If no active, use most recent by endDate
              mostRecent = subscriptions.sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
            }
            if (mostRecent && mostRecent.autoRenew === false) {
              showPurchaseButton = true;
            }
          }
          return showPurchaseButton ? (
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-primary" onClick={handlePurchaseSubscription}>
                <i className="fas fa-shopping-cart me-2"></i>
                Purchase Subscription
              </button>
            </div>
          ) : null;
        })()}
        {loading ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading subscription history...</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center">
            <p>No active subscriptions found. Purchase a subscription to access lessons and resources.</p>
            <button className="btn btn-primary" onClick={handlePurchaseSubscription}>Browse Plans</button>
          </div>
        ) : (
          <div className="subscriptions-row">
            {subscriptions.map((subscription) => {
              return (
              <div key={subscription._id} className="subscriptions-col-md-4">
                <div className="schooladmin-subscription-card">
                  <div className="schooladmin-subscription-card-body">
                    <h5 className="card-title">{subscription.name || subscription.planName || subscription.planId?.name || 'Unknown Plan'}</h5>
                    <h6 className="card-subtitle mb-2 text-muted">Status: {subscription.status || 'active'}</h6>
                    <p className="card-content">
                      <strong>Subscription ID:</strong> {subscription._id}<br/>
                      <strong>Start Date:</strong> {new Date(subscription.startDate).toLocaleDateString()}<br/>
                      <strong>End Date:</strong> {new Date(subscription.endDate).toLocaleDateString()}<br/>
                      <strong>Plan Type:</strong> {subscription.subType || subscription.planType}
                    </p>
                    
                    {subscription.documentUrl && !(subscription.orgUserId || subscription.organizationId) && (
                      <div className="mb-2">
                        <a 
                          href={subscription.documentUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'var(--darkPurple)',
                            borderColor: 'var(--darkOrange)',
                            color: 'var(--darkOrange)',
                            fontWeight: 'bold'
                          }}
                        >
                          📄 View Invoice/Receipt
                        </a>
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center">
                      {/* Check if this is an organization-assigned subscription */}
                      {(() => {
                        const isOrgAssigned = subscription.orgUserId || subscription.organizationId;
                        const isWorkshop = (subscription.subType || subscription.planType) === 'workshop';
                        
                        if (isOrgAssigned) {
                          return (
                            <div className="text-muted">
                              <small>
                                <i className="fas fa-building me-1"></i>
                                Managed by Organization
                              </small>
                            </div>
                          );
                        }
                        
                        if (!isWorkshop) {
                          return (
                            <>
                              <div className="form-check">
                                <input 
                                  className="form-check-input" 
                                  type="checkbox" 
                                  id={`autoRenew-${subscription._id}`}
                                  checked={subscription.autoRenew !== false}
                                  onChange={(e) => handleAutoRenewChange(subscription._id, e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`autoRenew-${subscription._id}`}>
                                  Auto Renew
                                </label>
                              </div>
                              {!subscription.autoRenew && (() => {
                                const endDate = new Date(subscription.endDate);
                                const currentDate = new Date();
                                const thirtyDaysFromNow = new Date(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));
                                const shouldShowRenew = endDate <= thirtyDaysFromNow;
                                return shouldShowRenew;
                              })() && (
                                <button 
                                  className="btn btn-outline-success"
                                  onClick={() => handleRenewSubscription(subscription)}
                                >
                                  Renew
                                </button>
                              )}
                            </>
                          );
                        }
                        
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Plans Tab */}
      <div tabId="plans" className="plans-tab-content">
        <h1 className="centered-header">Available Plans</h1>
        <div className="container">
          <div className="subscriptions-row">
            {plans
              .filter((plan) => plan.type !== 'workshop')
              .map((plan) => (
                <div key={plan._id} className="subscriptions-col-md-4">
                  <div className="subscriptions-card overflow-auto">
                    <div className="subscriptions-card-body">
                      <div
                        className={`d-flex ${
                          plan.name === '30-day FREE trial'
                            ? 'justify-content-center'
                            : 'justify-content-between'
                        } mb-3`}
                      >
                        <button
                          className="subscriptions-btn btn btn-primary"
                          onClick={() => handleAddToBasket(plan._id)}
                          disabled={plan.assets.length === 0}
                        >
                          {plan.name === '30-day FREE trial'
                            ? 'Start your Free Trial Now'
                            : 'Buy Subscription'}
                        </button>
                      </div>

                      <h4 className="subscriptions-card-title">{plan.name}</h4>
                      {plan.name !== '30-day FREE trial' && (
                        <>
                          <p>
                            Duration:{' '}
                            {plan.duration < 30
                              ? `${plan.duration} days`
                              : plan.duration % 365 === 0
                              ? `${plan.duration / 365} year${
                                  plan.duration / 365 > 1 ? 's' : ''
                                }`
                              : `${Math.ceil(plan.duration / 30)} months`}
                          </p>
                          <p>Price: {plan.price}</p>
                          {plan.description && <p>Description: {plan.description}</p>}
                        </>
                      )}
                      <br />
                      {plan.type !== 'workshop' && <h5>Lessons:</h5>}
                      <br />
                      <ul className="subscribed-asset">
                        {plan.assets?.map((asset) => (
                          <li key={asset._id}>
                            <h5>{asset.name}</h5>
                            <p>{asset.description}</p>
                            {asset.video?.thumbnail && (
                              <img
                                src={asset.video.thumbnail}
                                alt={`Thumbnail for ${asset.name}`}
                                className="asset-thumbnail thumbnail"
                                data-asset-id={asset._id}
                                style={{ cursor: 'default' }}
                              />
                            )}
                            {asset.file?.url && !asset.video?.thumbnail && (
                              <video
                                height="200"
                                width="280"
                                className="asset-video"
                                id={`video-${asset._id}`}
                                muted
                                loop
                                style={{ pointerEvents: 'none' }}
                              >
                                <source src={asset.file.url} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            )}
                            {asset.documents?.map((doc, index) => (
                              doc.url && (
                                <div key={index}>
                                  <span
                                    style={{ 
                                      color: '#FFB84D', 
                                      textDecoration: 'none',
                                      cursor: 'default'
                                    }}
                                  >
                                    {assetFileDisplayName(doc)} (Preview only)
                                  </span>
                                  <br />
                                </div>
                              )
                            ))}
                            <hr />
                          </li>
                        ))}
                      </ul>
                      <div className="buy-btns">
                        <div className="button-container">
                          <button
                            className="subscriptions-btn btn btn-primary"
                            onClick={() => handleAddToBasket(plan._id)}
                            disabled={plan.assets.length === 0}
                          >
                            {plan.name === '30-day FREE trial'
                              ? 'Start your Free Trial Now'
                              : 'Buy Subscription'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <br />
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Students Tab */}
      <div tabId="students" className="students-tab-content">
        <div className="row">
          <div className="col-md-12">
            <h4 className="section-title" style={{ textAlign: 'center' }}>All Students</h4>
            <p className="section-description" style={{ textAlign: 'center' }}>Students registered with your school.</p>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center">
            <p>Loading students...</p>
          </div>
        ) : students.length > 0 ? (
          <>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <form onSubmit={handleStudentSearch} className="form-inline">
                <input
                  className="form-control mr-sm-2"
                  type="search"
                  name="search"
                  placeholder="Student Name, Email, or Student Code"
                  aria-label="Search"
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                <button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                  Search
                </button>
              </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="studentsPerPage" className="form-label me-2 mb-0">Students per page:</label>
                  <select 
                    id="studentsPerPage"
                    className="form-select"
                    value={studentItemsPerPage}
                    onChange={(e) => setStudentItemsPerPage(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((studentCurrentPage - 1) * studentItemsPerPage) + 1} to {Math.min(studentCurrentPage * studentItemsPerPage, getFilteredStudents().length)} of {getFilteredStudents().length} students
                </small>
              </div>
            </div>

            <div className="students-grid">
              {getPaginatedStudents().map((student) => (
                <div key={student._id} className="content-card student-card">
                  <div className="student-content">
                    <h5 className="student-name">{student.contactName}</h5>
                    <div className="student-details">
                      <div className="student-info">
                        <span className="text-dark-orange">Email:</span>
                        <span className="text-light-orange">{student.email}</span>
                      </div>
                      <div className="student-info">
                        <span className="text-dark-orange">Student Code:</span>
                        <span className="text-light-orange">{student.studentCode}</span>
                      </div>
                      <div className="student-info">
                        <span className="text-dark-orange">Year Group:</span>
                        <span className="text-light-orange">{student.yearGrade}</span>
                      </div>
                      <div className="student-info">
                        <span className="text-dark-orange">Status:</span>
                        <span className={`text-${student.status === 'active' ? 'light-orange' : 'danger'}`}>
                          {student.status}
                        </span>
                      </div>
                    </div>
                    <div className="student-actions mt-3">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/student/${student._id}?from=school-admin`)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {getFilteredStudents().length > 0 && (
              <StandardPagination
                currentPage={studentCurrentPage}
                totalPages={Math.ceil(getFilteredStudents().length / studentItemsPerPage)}
                onPageChange={handleStudentPageChange}
                itemsPerPage={studentItemsPerPage}
                onItemsPerPageChange={setStudentItemsPerPage}
                totalItems={getFilteredStudents().length}
                itemsPerPageLabel="Students per page:"
                pageInfoLabel="Showing"
                showItemsPerPage={false}
              />
            )}
          </>
        ) : (
          <div className="text-center">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">No Students Found</h4>
                <p className="card-text">No students are currently registered with your school.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div tabId="teachers" className="teachers-tab-content">
        <div className="row">
          <div className="col-md-12">
            <h4 className="section-title" style={{ textAlign: 'center' }}>All Teachers</h4>
            <p className="section-description" style={{ textAlign: 'center' }}>Teachers registered with your school.</p>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center">
            <p>Loading teachers...</p>
          </div>
        ) : teachers.length > 0 ? (
          <>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <form onSubmit={handleTeacherSearch} className="form-inline">
                <input
                  className="form-control mr-sm-2"
                  type="search"
                  name="search"
                  placeholder="Teacher Name, Email, or Teacher Code"
                  aria-label="Search"
                  value={teacherSearchQuery}
                  onChange={(e) => setTeacherSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                <button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                  Search
                </button>
              </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="teachersPerPage" className="form-label me-2 mb-0">Teachers per page:</label>
                  <select 
                    id="teachersPerPage"
                    className="form-select"
                    value={teacherItemsPerPage}
                    onChange={(e) => setTeacherItemsPerPage(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((teacherCurrentPage - 1) * teacherItemsPerPage) + 1} to {Math.min(teacherCurrentPage * teacherItemsPerPage, getFilteredTeachers().length)} of {getFilteredTeachers().length} teachers
                </small>
              </div>
            </div>

            <div className="teachers-grid">
              {getPaginatedTeachers().map((teacher) => (
                <div key={teacher._id} className="content-card teacher-card">
                  <div className="teacher-content">
                    <h5 className="teacher-name">{teacher.contactName}</h5>
                    <div className="teacher-details">
                      <div className="teacher-info">
                        <span className="text-dark-orange">Email:</span>
                        <span className="text-light-orange">{teacher.email}</span>
                      </div>
                      <div className="teacher-info">
                        <span className="text-dark-orange">Teacher Code:</span>
                        <span className="text-light-orange">{teacher.tchCode || teacher.teacherCode || 'Not assigned'}</span>
                      </div>
                      <div className="teacher-info">
                        <span className="text-dark-orange">Status:</span>
                        <span className="text-light-orange">{teacher.status || 'active'}</span>
                      </div>
                    </div>
                    <div className="teacher-actions mt-3">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleViewTeacherDetails(teacher)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {getFilteredTeachers().length > 0 && (
              <StandardPagination
                currentPage={teacherCurrentPage}
                totalPages={Math.ceil(getFilteredTeachers().length / teacherItemsPerPage)}
                onPageChange={handleTeacherPageChange}
                itemsPerPage={teacherItemsPerPage}
                onItemsPerPageChange={setTeacherItemsPerPage}
                totalItems={getFilteredTeachers().length}
                itemsPerPageLabel="Teachers per page:"
                pageInfoLabel="Showing"
                showItemsPerPage={false}
              />
            )}
          </>
        ) : (
          <div className="text-center">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">No Teachers Found</h4>
                <p className="card-text">No teachers are currently registered with your school.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Workshop Tab */}
      <div tabId="workshops" className="workshop-tab-content">
        <div className="row">
          <div className="col-md-12">
            <h4 className="section-title" style={{ textAlign: 'center' }}>Available Workshops</h4>
            <p className="section-description" style={{ textAlign: 'center' }}>Enhance your teaching skills with our professional development workshops.</p>
          </div>
        </div>
        <div className="subscriptions-grid">
          {workshopPlans.map((workshop) => (
            <div key={workshop._id} className="content-card workshop-card-fixed">
              <div className="workshop-content">
                <div className="workshop-header">
                  <h5 className="workshop-title">{workshop.name}</h5>
                  <div className="workshop-header-controls">
                    <input
                      type="date"
                      id={`workshop-date-header-${workshop._id}`}
                      className={`form-control form-control-sm ${workshopErrors[workshop._id] ? 'is-invalid' : ''}`}
                      style={{marginBottom: 5, marginRight: 10}}
                      min={getMinWorkshopDate()}
                      value={workshop.selectedDate || ''}
                      onChange={e => {
                        const selectedDate = e.target.value;
                        const validation = validateWorkshopDate(selectedDate);
                        
                        if (validation.isValid) {
                          setWorkshopPlans(prev => prev.map(w => w._id === workshop._id ? { ...w, selectedDate: selectedDate } : w));
                          setWorkshopErrors(prev => ({ ...prev, [workshop._id]: '' }));
                        } else {
                          setWorkshopPlans(prev => prev.map(w => w._id === workshop._id ? { ...w, selectedDate: '' } : w));
                          setWorkshopErrors(prev => ({ ...prev, [workshop._id]: validation.message }));
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      className="btn btn-primary btn-sm workshop-buy-top"
                      disabled={!workshop.selectedDate || workshopErrors[workshop._id]}
                      onClick={async () => {
                        if (!workshop.selectedDate) return;
                        
                        const validation = validateWorkshopDate(workshop.selectedDate);
                        if (!validation.isValid) {
                          setWorkshopErrors(prev => ({ ...prev, [workshop._id]: validation.message }));
                          return;
                        }
                        
                        try {
                          const response = await axios.post(`/api/sch-add-to-basket/${workshop._id}`, {
                            quantity: 1,
                            workshopDate: workshop.selectedDate
                          }, { withCredentials: true });
                          if (response.data.success) {
                            window.location.href = response.data.redirectUrl || '/basket';
                          } else {
                            setWorkshopErrors(prev => ({ ...prev, [workshop._id]: 'Failed to add workshop to basket. Please try again.' }));
                          }
                        } catch (error) {
                          setWorkshopErrors(prev => ({ ...prev, [workshop._id]: 'Error adding workshop to basket. Please try again.' }));
                        }
                      }}
                    >
                      Buy Workshop
                    </button>
                  </div>
                </div>
                <div className="workshop-details">
                  <div className="workshop-info">
                    <span className="text-dark-orange">Duration:</span>
                    <span className="text-light-orange">{workshop.duration}</span>
                  </div>
                  <div className="workshop-info">
                    <span className="text-dark-orange">Price:</span>
                    <span className="text-light-orange">{workshop.price}</span>
                  </div>
                </div>
                <div className="workshop-description">
                  {(workshop.description || '')
                    .split(/\n\n+/)
                    .map(s => s.trim())
                    .filter(Boolean)
                    .map((para, i) => (
                      <p key={i} className="workshop-description-para">{para}</p>
                    ))}
                </div>
                <div className="workshop-buy-section">
                  <label htmlFor={`workshop-date-${workshop._id}`} className="text-dark-orange" style={{marginBottom: 4}}>Select Date:</label>
                <input
                  type="date"
                  id={`workshop-date-${workshop._id}`}
                  className={`form-control ${workshopErrors[workshop._id] ? 'is-invalid' : ''}`}
                  style={{marginBottom: 10}}
                  min={getMinWorkshopDate()}
                  value={workshop.selectedDate || ''}
                                      onChange={e => {
                      const selectedDate = e.target.value;
                      const validation = validateWorkshopDate(selectedDate);
                      
                      if (validation.isValid) {
                        setWorkshopPlans(prev => prev.map(w => w._id === workshop._id ? { ...w, selectedDate: selectedDate } : w));
                        setWorkshopErrors(prev => ({ ...prev, [workshop._id]: '' }));
                      } else {
                        setWorkshopPlans(prev => prev.map(w => w._id === workshop._id ? { ...w, selectedDate: '' } : w));
                        setWorkshopErrors(prev => ({ ...prev, [workshop._id]: validation.message }));
                        e.target.value = '';
                      }
                    }}
                />
                {workshopErrors[workshop._id] && (
                  <div className="invalid-feedback d-block" style={{color: '#ff8c42', fontSize: '0.875rem', marginTop: '0.25rem', marginBottom: '10px'}}>
                    {workshopErrors[workshop._id]}
                  </div>
                )}
                {workshop.selectedDate && (
                  <div className="mt-1" style={{marginBottom: 10}}>
                    <span className="text-dark-orange">Selected Date:</span> <span className="text-light-orange">{workshop.selectedDate}</span>
                  </div>
                )}
                <button
                  className="btn btn-primary"
                  disabled={!workshop.selectedDate || workshopErrors[workshop._id]}
                  onClick={async () => {
                    if (!workshop.selectedDate) return;
                    
                    const validation = validateWorkshopDate(workshop.selectedDate);
                    if (!validation.isValid) {
                      setWorkshopErrors(prev => ({ ...prev, [workshop._id]: validation.message }));
                      return;
                    }
                    
                    try {
                      const response = await axios.post(`/api/sch-add-to-basket/${workshop._id}`, {
                        quantity: 1,
                        workshopDate: workshop.selectedDate
                      }, { withCredentials: true });
                      if (response.data.success) {
                        window.location.href = response.data.redirectUrl || '/basket';
                      } else {
                        setWorkshopErrors(prev => ({ ...prev, [workshop._id]: 'Failed to add workshop to basket. Please try again.' }));
                      }
                    } catch (error) {
                      setWorkshopErrors(prev => ({ ...prev, [workshop._id]: 'Error adding workshop to basket. Please try again.' }));
                    }
                  }}
                >
                  Buy Workshop
                </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {workshopPlans.length === 0 && (
          <div className="text-center">
            <p>No workshops available at the moment. Check back later for new workshop opportunities.</p>
          </div>
        )}
      </div>

      <div tabId="users" className="users-tab-content">
        <div className="text-center">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">User Management</h4>
              <p className="card-text">User management features will be available once you have an active subscription.</p>
            </div>
          </div>
        </div>
      </div>

    </BaseDashboard>
    
    {/* Join Organization Modal - OUTSIDE BaseDashboard */}
    {showJoinModal && (
      <div 
        className="join-modal-overlay"
        onClick={() => setShowJoinModal(false)}
      >
        <div 
          className="join-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="join-modal-header">
            <h3 className="join-modal-title">Join Organization</h3>
            <button 
              className="join-modal-close-btn"
              onClick={handleCloseModal}
            >
              ×
            </button>
          </div>
          
          <div className="join-modal-body">
            <form onSubmit={handleJoinOrgSubmit}>
              <div className="join-modal-form-group">
                <label className="join-modal-label">Organization Code</label>
                <input
                  type="text"
                  className="join-modal-input"
                  placeholder="Enter your organization code"
                  value={orgCodeInput}
                  onChange={(e) => setOrgCodeInput(e.target.value)}
                  required
                />
                <div className="join-modal-help-text">
                  {isAutoPopup 
                    ? "As a standalone school, you can join an organization to access their subscription benefits. Enter the organization code provided by your organization administrator."
                    : "Enter the organization code provided by your organization administrator to join their subscription benefits."
                  }
                </div>
              </div>
              
              {orgCodeError && (
                <div className="join-modal-error">
                  {orgCodeError}
                </div>
              )}
            </form>
          </div>
          
          <div className="join-modal-footer">
            <button 
              type="button"
              className="join-modal-button join-modal-button-secondary" 
              onClick={handleCloseModal}
            >
              {isAutoPopup ? 'Continue Standalone' : 'Cancel'}
            </button>
            <button 
              type="submit" 
              className="join-modal-button join-modal-button-primary"
              disabled={joiningOrg}
              onClick={handleJoinOrgSubmit}
            >
              {joiningOrg ? 'Joining...' : 'Join Organization'}
            </button>
          </div>
        </div>
      </div>
    )}
    
    <RenewalModal
      isOpen={showRenewalModal}
      onClose={handleCloseRenewalModal}
      currentSubscription={selectedSubscription}
      onRenewalSuccess={handleRenewalSuccess}
    />
    </>
  );
};

export default SchoolAdminDashboard; 